import { RefObject, useCallback } from 'react';

import TableBottomLoader from '#/components/tables/TableBottomLoader';
import { useWsStatus } from '@paradigm/features/src/unified-rfqs/contexts/use-ws-status';
import { toast } from '#/components/UnifiedRfqs/toast';
import {
  ListReq,
  MergedSourcesFetcher,
  Status,
} from '@paradigm/features/src/unified-rfqs/services/merged-sources-fetcher';
import { BaseEntity } from '@paradigm/features/src/unified-rfqs/store/entity';
import { useDebouncedTrackAction } from '@paradigm/features/src/analytics';

interface Props<T extends MergedSourcesFetcher<ListReq, BaseEntity>> {
  readonly scrollableRef: RefObject<HTMLElement>;
  readonly fetcher: T;
  readonly canFetchMore: boolean;
  readonly status: Status;
}

export default function BaseBottomLoader<
  T extends MergedSourcesFetcher<ListReq, BaseEntity>,
>(props: Props<T>) {
  const { scrollableRef, fetcher, canFetchMore, status } = props;
  const { subscribing, connected } = useWsStatus();
  const trackAction = useDebouncedTrackAction();

  const handleFetchMore = useCallback(() => {
    trackAction('load more items');
    const onError = ({ message }: { message: string }) => {
      toast(message);
    };

    fetcher.fetchMore({}, onError);
  }, [fetcher, trackAction]);

  return (
    <TableBottomLoader
      disabled={!canFetchMore}
      isLoading={!connected || subscribing || status !== 'idle'}
      scrollableRef={scrollableRef}
      onFetchMore={handleFetchMore}
    />
  );
}
